import {
  advancedFormControl,
  FormControlTypeEnum,
} from '@magieno/common';
import { translationBaseKey } from '../../../decorators/translation-base-key.decorator';
import { FieldTypeEnum } from '../../../enums/field-type.enum';
import { IsBoolean, IsNotEmpty, IsString, ValidateIf } from '@pristine-ts/class-validator';
import { ErrorMessageBuilder } from '../../../builders/error-message.builder';

@translationBaseKey('rollover-required-information-properties-transferred-options')
export class RolloverRequiredInformationPropertiesTransferredOptions {
  propertyTransferredId: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que le bien transféré est une action d’une société?',
        en: 'Is the transferred property a share of a corporation?',
      },
      tooltip: {
        fr: 'Ce champ indique si le bien transféré est une action d\'une société. Si activé, cela signifie que le bien est une action de la société et les détails supplémentaires doivent être fournis. Si désactivé, cela signifie que le bien transféré n\'est pas une action de la société.',
        en: 'This field indicates whether the transferred property is a share of a corporation. If enabled, it means that the property is a share of the corporation, and additional details need to be provided. If disabled, it means that the transferred property is not a share of the corporation.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areShares: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le nom de la société dont les actions sont vendues?',
        en: 'What is the name of the corporation whose shares are being sold?',
      },
      tooltip: {
        fr: 'Ce champ représente le nom de la société dont les actions sont vendues. Il est requis uniquement si le bien transféré est une action d\'une société. Le nom de la société est utilisé pour l\'identification fiscale et les rapports sur la vente d\'actions.',
        en: 'This field represents the name of the corporation whose shares are being sold. It is required only if the transferred property is a share of a corporation. The corporation\'s name is used for tax identification and reporting purposes for the share sale.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @ValidateIf(o => o.areShares)
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  sellingSharesCorporationName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le numéro d\'entreprise?',
        en: 'What is the business number?',
      },
      tooltip: {
        fr: 'Ce champ représente le numéro d\'entreprise de la société dont les actions sont vendues. Il est requis uniquement si le bien transféré est une action d\'une société. Le numéro d\'entreprise est utilisé à des fins d\'identification fiscale et de suivi de l\'entreprise.',
        en: 'This field represents the business number of the corporation whose shares are being sold. It is required only if the transferred property is a share of a corporation. The business number is used for tax identification and tracking purposes for the corporation.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @ValidateIf(o => o.areShares)
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  sellingSharesCorporationBusinessNumber: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le numéro d\'entreprise?',
        en: 'What is the business number?',
      },
      tooltip: {
        fr: 'Numéro d\'entreprise de la corporation dont les actions sont vendues. Le Numéro d\'entreprise (NE) est utilisé par l\'Agence du revenu du Canada (ARC) pour identifier les sociétés aux fins fiscales.',
        en: 'Business number of the corporation whose shares are being sold. The Business Number (BN) is used by the Canada Revenue Agency (CRA) to identify corporations for tax purposes. Learn more: [CRA - Business Number (BN)](https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/registering-your-business/obtain-a-business-number.html)',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @ValidateIf(o => o.areShares)
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  sellingSharesCorporationRCNumber: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le numéro d\'identification?',
        en: 'What is the identification number?',
      },
      tooltip: {
        fr: 'Numéro d\'identification de la corporation dont les actions sont vendues. Ce numéro peut être utilisé pour identifier la société à des fins internes ou être un identifiant délivré par le gouvernement en fonction des réglementations fiscales.',
        en: 'Identification number of the corporation whose shares are being sold. This number may be used to identify the corporation for internal purposes or be a government-issued identifier based on tax regulations.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @ValidateIf(o => o.areShares)
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  sellingSharesCorporationIdentificationNumber: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le numéro d\'identification?',
        en: 'What is the identification number?',
      },
      tooltip: {
        fr: 'Numéro d\'entreprise du Québec (NEQ) de la corporation dont les actions sont vendues. Le NEQ est un identifiant unique attribué par le Registraire des entreprises du Québec aux entreprises opérant au Québec.',
        en: 'Quebec Enterprise Number (NEQ) of the corporation whose shares are being sold. The NEQ is a unique identifier assigned by the Registraire des entreprises du Québec to businesses operating in Quebec. Learn more: [Registraire des entreprises du Québec - NEQ](https://www.registraireentreprises.gouv.qc.ca/en/centre_info/num_entreprise.html)',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @ValidateIf(o => o.areShares)
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  sellingSharesCorporationQuebecEnterpriseNumber: string;
}
